/**
 * URL <-> view-state codec for the case workspace (GEN-2922).
 *
 * Makes filter, sort, and the selected variant fully URL-addressable so a case
 * view is shareable and survives reload. `decode(encode(state)) === state` is
 * covered by a round-trip unit test. Unrelated params (e.g. ?family=) are left
 * untouched by callers, which merge these keys into the existing params.
 */

import {
  type SortDir,
  type SortKey,
  type VariantFilters,
  type VariantSort,
  DEFAULT_SORT,
  emptyFilters,
} from './variantQuery'

export type WorkspaceState = {
  filters: VariantFilters
  sort: VariantSort
  selectedVariantId: string | null
  /** Selected variant track id; null = the API's default track. */
  track: string | null
}

const PARAM_KEYS = [
  'variant',
  'filter',
  'tag',
  'chrom',
  'minQual',
  'minDp',
  'vafMin',
  'vafMax',
  'sort',
  'track',
] as const

const SORT_KEYS: SortKey[] = ['locus', 'qual', 'filter', 'tag', 'mlProb', 'gnomad']

function parseNumber(value: string | null): number | null {
  if (value === null || value.trim() === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function decodeState(params: URLSearchParams): WorkspaceState {
  const filters = emptyFilters()
  filters.filters = params.getAll('filter')
  filters.tags = params.getAll('tag')
  filters.chroms = params.getAll('chrom')
  filters.minQual = parseNumber(params.get('minQual'))
  filters.minDp = parseNumber(params.get('minDp'))
  const vafMin = parseNumber(params.get('vafMin'))
  const vafMax = parseNumber(params.get('vafMax'))
  if (vafMin !== null) filters.vafMin = Math.min(Math.max(vafMin, 0), 1)
  if (vafMax !== null) filters.vafMax = Math.min(Math.max(vafMax, 0), 1)

  let sort: VariantSort = { ...DEFAULT_SORT }
  const rawSort = params.get('sort')
  if (rawSort) {
    const [key, dir] = rawSort.split(':')
    if (SORT_KEYS.includes(key as SortKey)) {
      sort = { key: key as SortKey, dir: dir === 'desc' ? 'desc' : ('asc' as SortDir) }
    }
  }

  return { filters, sort, selectedVariantId: params.get('variant'), track: params.get('track') }
}

/**
 * Apply workspace state onto a copy of `base` params, clearing any of our keys
 * that are at their default so the URL stays clean. Returns a new URLSearchParams.
 */
export function encodeState(state: WorkspaceState, base?: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(base?.toString() ?? '')
  for (const key of PARAM_KEYS) params.delete(key)

  const { filters, sort, selectedVariantId, track } = state
  for (const f of filters.filters) params.append('filter', f)
  for (const t of filters.tags) params.append('tag', t)
  for (const c of filters.chroms) params.append('chrom', c)
  if (filters.minQual !== null) params.set('minQual', String(filters.minQual))
  if (filters.minDp !== null) params.set('minDp', String(filters.minDp))
  if (filters.vafMin > 0) params.set('vafMin', String(filters.vafMin))
  if (filters.vafMax < 1) params.set('vafMax', String(filters.vafMax))
  if (sort.key !== DEFAULT_SORT.key || sort.dir !== DEFAULT_SORT.dir) {
    params.set('sort', `${sort.key}:${sort.dir}`)
  }
  if (selectedVariantId) params.set('variant', selectedVariantId)
  if (track) params.set('track', track)

  return params
}
